import React, { memo, Suspense, lazy, useMemo } from 'react'
import { LiveKitVideoDisplayFactory } from './LiveKitVideoDisplayFactory'
import type { LiveKitVideoDisplayProps } from './types/LiveKitVideoDisplay.types'
import LiveKitErrorBoundary from '../LiveKitErrorBoundary'
import { LiveKitProximityProvider } from './context/LiveKitProximityContext'
import { LiveKitSafetyWrapper } from './LiveKitSafetyWrapper'

/**
 * LiveKitVideoDisplay — single public entry for in-room video UI.
 * Composes: ErrorBoundary → SafetyWrapper → Factory → Controller.
 *
 * Must be used inside a LiveKit RoomContext (see LiveKitRoomConnector).
 */
const LiveKitVideoDisplay: React.FC<LiveKitVideoDisplayProps> = memo(
  ({
    isVisible = true,
    isLoading = false,
    connectionState,
    isCharacterInitialized,
    ...restProps
  }) => {
    const isDebugMode = useMemo(() => {
      return (
        process.env.NODE_ENV === 'development' &&
        typeof window !== 'undefined' &&
        (window as any).LIVEKIT_DEBUG_MODE
      )
    }, [])

    if (isDebugMode) {
      console.log('[LiveKitVideoDisplay] Rendering with props:', {
        isVisible,
        isLoading,
        connectionState,
        isCharacterInitialized,
        ...restProps,
      })
    }

    return (
      <LiveKitErrorBoundary>
        <LiveKitProximityProvider>
        <LiveKitSafetyWrapper
          autoStart={true}
          checkInterval={3000}
          autoFixEnabled={true}
          enableAlerts={!isDebugMode}
          showHealthIndicator={isDebugMode}
          onHealthChange={(score) => {
            if (isDebugMode) {
              console.log('[LiveKitVideoDisplay] Health score:', score)
            }
          }}
          onIssueDetected={(issue) => {
            if (isDebugMode) {
              console.warn('[LiveKitVideoDisplay] Issue detected:', issue)
            }
          }}
          onFixApplied={(fix) => {
            if (isDebugMode) {
              console.log('[LiveKitVideoDisplay] Fix applied:', fix)
            }
          }}
        >
          <LiveKitVideoDisplayFactory
            isVisible={isVisible}
            isLoading={isLoading}
            connectionState={connectionState}
            isCharacterInitialized={isCharacterInitialized}
            {...restProps}
          />
        </LiveKitSafetyWrapper>
        </LiveKitProximityProvider>
      </LiveKitErrorBoundary>
    )
  },
  (prevProps, nextProps) => {
    const prevVisible = prevProps.isVisible ?? true
    const nextVisible = nextProps.isVisible ?? true
    const prevLoading = prevProps.isLoading ?? false
    const nextLoading = nextProps.isLoading ?? false

    return (
      prevProps.connectionState === nextProps.connectionState &&
      prevProps.isCharacterInitialized === nextProps.isCharacterInitialized &&
      prevVisible === nextVisible &&
      prevLoading === nextLoading
    )
  }
)

LiveKitVideoDisplay.displayName = 'LiveKitVideoDisplay'

export const LazyLiveKitVideoDisplay = lazy(() =>
  Promise.resolve({ default: LiveKitVideoDisplay })
)

export const LiveKitVideoDisplayWithSuspense: React.FC<LiveKitVideoDisplayProps> = (props) => (
  <Suspense
    fallback={
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          color: '#666',
          fontSize: '14px',
        }}
      >
        Loading video display...
      </div>
    }
  >
    <LiveKitVideoDisplay {...props} />
  </Suspense>
)

LiveKitVideoDisplayWithSuspense.displayName = 'LiveKitVideoDisplayWithSuspense'

export { LiveKitVideoDisplayFactory } from './LiveKitVideoDisplayFactory'
export {
  LiveKitProximityProvider,
  useLiveKitProximity,
  useLiveKitProximityOptional,
} from './context/LiveKitProximityContext'
export {
  LiveKitCallControlsProvider,
  useLiveKitCallControls,
  useLiveKitCallControlsRegistration,
} from './context/LiveKitCallControlsContext'
export {
  LiveKitSafetyWrapper,
  withLiveKitSafety,
  useLiveKitSafetyControl,
} from './LiveKitSafetyWrapper'

export type {
  LiveKitVideoDisplayProps,
  VideoControlProps,
  VideoControlSurface,
  ParticipantVideoProps,
  VideoDisplayState,
  VideoDisplayActions,
  ProximityState,
  VideoPosition,
  VideoElementRefs,
} from './types/LiveKitVideoDisplay.types'

export default LiveKitVideoDisplay
